import {
  EditStrategyFormFields,
  EditStrategyInitContext,
  EditStrategySubmitContext,
  TariffAcOption,
  TariffAfOption,
} from './types';
import { AggregateStrategy, ServicePackage } from '../../../../../../types/aggregateStrategy';
import { PackageOption } from '../../../../../../types/option';

export const autofollowKeyPrefix = 'autofollow_package_';
export const autoconsaltKeyPrefix = 'autoconsalt_package_';

export function getDefaultEditStrategyFormValues(): EditStrategyFormFields {
  return {
    id: undefined,
    title: '',
    tagline: '',
    author: { id: '', name: '' },
    managerIds: [],
    isBlogger: false,
    infoParts: [],
    riskReasons: [],
    imageUrl: '',
    videoUrl: '',
    matchmakerVideoUrl: '',
    description: '',
    disclaimer: '',
    welcomeLetter: '',
    subscription: '',
    colour: '',
    profile: undefined,
    currency: { code: '', name: '', symbol: null },
    minSumma: 0,
    recommendedAmount: undefined,
    repoWithCcp: false,
    capacity: undefined,
    estimatedProfit: undefined,
    estimatedTradesPerWeek: undefined,
    period: { id: 0, name: '', period: 0 },
    category: { id: 1, name: '' },
    markets: [],
    tools: [],
    boards: [],
    securities: [],
    maxSignalWeights: [],
    maxInstrumentWeights: [],
    index: undefined,
    hideIndexOnChart: false,
    hideIndexInWL: false,
    isAutofollow: false,
    tariffAf: undefined,
    isAutoconsult: false,
    tariffAc: undefined,
    isAutofollowPS: false,
    isAutoconsultPS: false,
    rateShort: null,
    rateLong: null,
    maxInstrumentWeight: 0,
    maxSignalWeight: undefined,
    drawdown: '',
    leverage: 0,
    shortLeverage: null,
    recalcMode: { id: 0, name: '' },
    tradeType: undefined,
    plStartDate: undefined,
    publishingDate: undefined,
    isAlgo: false,
    testMode: false,
    closed: false,
    isRestricted: false,
    restrictedClients: [],
    tests: [],
    codeDWH: '',
    productPlanKO: '',
    isIis: false,
    isCrisis: false,
    isRecommended: false,
    isHidden: false,
    stockMarketMMVB: false,
    fortsRTS: false,
    creditGO: false,
    currencyMarket: false,
    spbRTS: false,
    currencyMarketEBS: false,
    stockMarketOffexchange: false,
    mmvbUSD: false,
    fortsRTSEBS: false,
    lse: false,
    usMarkets: false,
  };
}

export function areAllPackagesCovered(
  packages: PackageOption[],
  formValues: Record<string, unknown>,
  prefix: string = autofollowKeyPrefix,
): boolean {
  return packages.every((pkg) => {
    const prefixKey = `${prefix}${pkg.id}`;
    return !!formValues[prefixKey];
  });
}

export function mapFormValuesToServicePackages(
  packages: PackageOption[],
  formValues: Record<string, unknown>,
): ServicePackage[] {
  const result: ServicePackage[] = [];
  const values = formValues as Record<string, { id: number } | boolean | undefined>;

  packages.forEach((pkg) => {
    const autofollowKey = `${autofollowKeyPrefix}${pkg.id}`;
    const autoconsaltKey = `${autoconsaltKeyPrefix}${pkg.id}`;

    if (values.isAutofollowPS && values[autofollowKey] && typeof values[autofollowKey] !== 'boolean') {
      result.push({
        servicePackageId: Number(pkg.id),
        tariffId: values[autofollowKey].id,
        strategyType: 1,
      });
    }

    if (values.isAutoconsultPS && values[autoconsaltKey] && typeof values[autoconsaltKey] !== 'boolean') {
      result.push({
        servicePackageId: Number(pkg.id),
        tariffId: values[autoconsaltKey].id,
        strategyType: 2,
      });
    }
  });

  return result;
}

export function initializeServicePackageTariffFields(
  servicePackages: ServicePackage[],
  tariffs: { id: number; name: string }[],
  setFieldValue: (field: string, value: unknown) => void,
): void {
  servicePackages.forEach((item) => {
    const key =
      item.strategyType === 1
        ? `${autofollowKeyPrefix}${item.servicePackageId}`
        : `${autoconsaltKeyPrefix}${item.servicePackageId}`;

    const tariff = tariffs.find((tariffItem) => tariffItem.id === item.tariffId);

    if (tariff) {
      setFieldValue(key, tariff);
    } else {
      console.warn(`Tariff with ID ${item.tariffId} not found for service package ${item.servicePackageId}`);
    }
  });
}

function getTariffAf(id: number | undefined, tariffs: EditStrategyInitContext['tariffs']): TariffAfOption | undefined {
  if (!id) {
    return undefined;
  }

  const tariff = tariffs.find((item) => item.id === id);
  if (!tariff) {
    return undefined;
  }

  return {
    id: tariff.id,
    name: tariff.description,
    guid: tariff.guid,
    category: tariff.category ?? '',
  };
}

function getTariffAc(id: number | undefined, tariffs: EditStrategyInitContext['tariffs']): TariffAcOption | undefined {
  if (!id) {
    return undefined;
  }

  const tariff = tariffs.find((item) => item.id === id);
  if (!tariff) {
    return undefined;
  }

  return {
    id: tariff.id,
    name: tariff.description,
    guid: tariff.guid,
  };
}

function getTradeType(
  id: number,
  tradeTypes: EditStrategyInitContext['infoStore']['tradeTypes'],
): { id: number; name: string } | undefined {
  const tradeType = tradeTypes.find((item) => item.id === id && item.enabled);
  if (!tradeType) {
    return undefined;
  }

  return {
    id: tradeType.id,
    name: tradeType.description,
  };
}

export function mapStrategyToFormValues({
  strategy,
  infoStore,
  currencyStore,
  users,
  tariffs,
  recalcModes,
  categories,
}: EditStrategyInitContext): Partial<EditStrategyFormFields> {
  const fieldValues: Partial<EditStrategyFormFields> = {
    id: strategy.id,
    title: strategy.name,
    tagline: strategy.tagline ?? '',
    infoParts: strategy.infoParts ?? [],
    riskReasons: strategy.riskReasons ?? [],
    imageUrl: strategy.imageUrl ?? '',
    videoUrl: strategy.videoUrl ?? '',
    matchmakerVideoUrl: strategy.matchmakerVideoUrl ?? '',
    description: strategy.descriptionHtml ?? '',
    disclaimer: strategy.disclaimer ?? '',
    welcomeLetter: strategy.goal ?? '',
    subscription: strategy.chartComment,
    profile:
      strategy.minInvestProfileId >= 0
        ? infoStore.profiles.find((item) => item.id === strategy.minInvestProfileId)
        : undefined,
    currency: strategy.currency ? currencyStore.find((item) => item.code === strategy.currency) : undefined,
    minSumma: strategy.subscriptionThreshold,
    recommendedAmount: strategy.recommendedAmount,
    repoWithCcp: strategy.repoWithCcp,
    capacity: strategy.capacity,
    estimatedProfit: strategy.estimatedProfit,
    estimatedTradesPerWeek: strategy.estimatedTradesPerWeek,
    period: strategy.durationId >= 0 ? infoStore.durations.find((item) => item.id === strategy.durationId) : undefined,
    category: strategy.forQualifiedInvestorsOnly ? categories[0] : categories[1],
    markets: strategy.marketIds
      ? infoStore.markets.filter((item) => strategy.marketIds.find((marketId) => marketId === item.id))
      : [],
    tools: strategy.marketToolIds
      ? infoStore.marketTools.filter((item) => strategy.marketToolIds.find((toolId) => toolId === item.id))
      : [],
    boards: strategy.boardIds
      ? infoStore.boards.filter((item) => strategy.boardIds.find((boardId) => boardId === item.id))
      : [],
    securities: [],
    maxSignalWeights: strategy.maxSignalWeights ?? [],
    maxInstrumentWeights: strategy.maxInstrumentWeights ?? [],
    index: strategy.indexId ? infoStore.indexes.find((item) => item.id === strategy.indexId) : undefined,
    hideIndexInWL: strategy.hideIndexInWL,
    hideIndexOnChart: strategy.hideIndexOnChart,
    isAutofollow: strategy.autofollow,
    isAutoconsult: strategy.autoconsult,
    tariffAf: strategy.afTariffId ? getTariffAf(strategy.afTariffId, tariffs) : undefined,
    tariffAc: strategy.acTariffId ? getTariffAc(strategy.acTariffId, tariffs) : undefined,
    maxInstrumentWeight: strategy.maxInstrumentWeight,
    maxSignalWeight: strategy.maxSignalWeight,
    leverage: strategy.leverage,
    shortLeverage: strategy.shortLeverage ?? null,
    recalcMode: recalcModes.find((item) => item.id === strategy.recalcMode),
    tradeType: strategy.tradeType ? getTradeType(strategy.tradeType, infoStore.tradeTypes) : undefined,
    plStartDate: strategy.plStartDate ? new Date(strategy.plStartDate) : undefined,
    publishingDate: strategy.publishingDate ? new Date(strategy.publishingDate) : undefined,
    isAlgo: strategy.isAlgostrategy,
    testMode: strategy.testMode,
    closed: !strategy.open,
    isIis: strategy.iis,
    isRecommended: strategy.recommended,
    isHidden: strategy.hidePortfolio,
    isRestricted: strategy.isRestricted,
    restrictedClients: strategy.clients ?? [],
    tests:
      strategy.tests
        ?.map((testId) => infoStore.tests.find((testItem) => testItem.id === testId))
        .filter((testItem) => !!testItem)
        .map((testItem) => ({ id: testItem?.id ?? '', name: testItem?.name ?? '' })) ?? [],
    rateShort: strategy.rateShort,
    rateLong: strategy.rateLong,
    stockMarketMMVB: strategy.evaParams.stockMarketMMVB,
    fortsRTS: strategy.evaParams.fortsRTS,
    creditGO: strategy.evaParams.creditGO,
    currencyMarket: strategy.evaParams.currencyMarket,
    spbRTS: strategy.evaParams.spbRTS,
    currencyMarketEBS: strategy.evaParams.currencyMarketEBS,
    stockMarketOffexchange: strategy.evaParams.stockMarketOffexchange,
    mmvbUSD: strategy.evaParams.mmvbUSD,
    fortsRTSEBS: strategy.evaParams.fortsRTSEBS,
    lse: strategy.evaParams.lse,
    usMarkets: strategy.evaParams.usMarkets,
    isBlogger: strategy.isBlogger,
    isAutofollowPS: strategy.autofollowPS,
    isAutoconsultPS: strategy.autoconsultPS,
    codeDWH: String(strategy.codeDWH || ''),
    productPlanKO: String(strategy.productPlanKO || ''),
  };

  const author = users.find((item) => item.id === String(strategy.managerId));
  if (author) {
    fieldValues.author = { id: author.id.toString(), name: author.name };
  }

  const managers = users.filter((item) => strategy.managerIds?.includes(Number(item.id)));
  if (managers.length) {
    fieldValues.managerIds = managers.map(({ id, name }) => ({ id: id.toString(), name: name }));
  }

  if (strategy.securities) {
    const securities = JSON.parse(strategy.securities) as string[];
    if (securities) {
      fieldValues.securities = securities.map((security) => ({ id: security, name: security, value: security }));
    }
  }

  return fieldValues;
}

export function mapFormValuesToUpdateRequest(
  values: EditStrategyFormFields,
  { packages }: EditStrategySubmitContext,
): Partial<AggregateStrategy> {
  const codeDWH = values.codeDWH === '' ? undefined : Number(values.codeDWH);
  const productPlanKO = values.productPlanKO === '' ? undefined : Number(values.productPlanKO);

  const data: Partial<AggregateStrategy> = {
    id: values.id ? values.id : '',
    name: values.title,
    tagline: values.tagline || '',
    managerId: Number(values.author.id),
    managerName: values.author.name,
    managerIds: values.managerIds?.map(({ id: managerId }) => Number(managerId)) || [],
    creationDate: new Date().toDateString(),
    currency: values.currency.code,
    leverage: values.leverage,
    recalcMode: values.recalcMode.id,
    tradeType: values.tradeType?.id ?? undefined,
    noWeightCorrection: 0,
    securities: JSON.stringify(values.securities ? values.securities.map((item) => item.value) : []),
    maxSignalWeights: values.maxSignalWeights,
    maxInstrumentWeights: values.maxInstrumentWeights,
    maxInstrumentWeight: values.maxInstrumentWeight,
    maxSignalWeight: values.maxSignalWeight ?? undefined,
    subscriptionThreshold: values.minSumma,
    recommendedAmount: values.recommendedAmount ?? undefined,
    repoWithCcp: values.repoWithCcp,
    capacity: values.capacity,
    estimatedProfit: values.estimatedProfit,
    estimatedTradesPerWeek: values.estimatedTradesPerWeek,
    shortLeverage: values.shortLeverage,
    active: true,
    rating: 0,
    autofollow: values.isAutofollow,
    autoconsult: values.isAutoconsult,
    afTariffId: values.tariffAf === undefined ? undefined : values.tariffAf.id,
    acTariffId: values.tariffAc === undefined ? undefined : values.tariffAc.id,
    category: values.tariffAf === undefined ? undefined : values.tariffAf.category,
    price: values.tariffAc === undefined ? null : values.tariffAc.name,
    chartComment: values.subscription,
    imageUrl: values.imageUrl ? values.imageUrl : null,
    videoUrl: values.videoUrl ? values.videoUrl : null,
    matchmakerVideoUrl: values.matchmakerVideoUrl ? values.matchmakerVideoUrl : null,
    descriptionHtml: values.description,
    disclaimer: values.disclaimer,
    durationId: values.period.id,
    estimatedDrawdown: 0,
    actualProfit: 0,
    forQualifiedInvestorsOnly: Number(values.category.id) === 0,
    marketIds: values.markets.map((item) => item.id),
    marketToolIds: values.tools.map((item) => item.id),
    boardIds: values.boards.map((item) => item.id),
    plStartDate: values.plStartDate?.toDateString(),
    publishingDate: values.publishingDate?.toDateString(),
    hidePortfolio: values.isHidden,
    iis: values.isIis,
    infoParts: [...values.infoParts],
    riskReasons: [...values.riskReasons],
    isAlgostrategy: values.isAlgo,
    isRestricted: values.isRestricted,
    maxIndustryWeight: 0,
    minInvestProfileId: values.profile == undefined ? undefined : values.profile.id,
    open: !values.closed,
    order: 1000,
    showFullPortfolio: false,
    recommended: values.isRecommended,
    testMode: values.testMode,
    goal: values.welcomeLetter,
    tradesFrequency: '',
    rateShort: values.rateShort !== 0 ? values.rateShort : null,
    rateLong: values.rateLong !== 0 ? values.rateLong : null,
    hideIndexInWL: values.hideIndexInWL,
    hideIndexOnChart: values.hideIndexOnChart,
    tests: values.tests?.map((item) => item.id) ?? [],
    evaParams: {
      stockMarketMMVB: values.stockMarketMMVB,
      fortsRTS: values.fortsRTS,
      creditGO: values.creditGO,
      currencyMarket: values.currencyMarket,
      spbRTS: values.spbRTS,
      currencyMarketEBS: values.currencyMarketEBS,
      stockMarketOffexchange: values.stockMarketOffexchange,
      mmvbUSD: values.mmvbUSD,
      fortsRTSEBS: values.fortsRTSEBS,
      lse: values.lse,
      usMarkets: values.usMarkets,
    },
    isBlogger: values.isBlogger,
    autofollowPS: values.isAutofollowPS,
    autoconsultPS: values.isAutoconsultPS,
    servicePackageTariffs: mapFormValuesToServicePackages(packages || [], values),
    codeDWH,
    productPlanKO,
  };

  if (values.index) {
    data.indexId = Number(values.index.id);
  }

  if (values.isRestricted) {
    data.clients = values.restrictedClients;
  }

  return data;
}
